import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { fetchApplicationInfoMapping } from '@services/applicationInfo';
import { useEffectiveProductivityApps } from '@/hooks/useEffectiveProductivityApps';
import { getAppNameByAppPath } from '@/utils/utility';
import { queryKeys } from '@config/queryKeys';

function fmtTime(s) {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function calculate(appUsage, totalSec, activeSec, focusApps, appNameMapping) {
  if (!appUsage || !focusApps?.length || !totalSec) return null;

  let focusSec = 0;
  for (const [path, secs] of Object.entries(appUsage)) {
    const name = getAppNameByAppPath(path, appNameMapping);
    if (focusApps.some((f) => f.toLowerCase() === name.toLowerCase())) {
      focusSec += secs;
    }
  }

  // appUsage isn't idle-aware (it counts foreground time even while the user
  // is idle), so raw focusSec can otherwise exceed the user's active time.
  // Cap it there — the difference is pushed into "Other" (distraction) time.
  focusSec = Math.min(focusSec, activeSec ?? totalSec, totalSec);
  const otherSec = totalSec - focusSec;
  const focusPct = Math.round((focusSec / totalSec) * 100);

  return {
    focusPct,
    label: 'Focus',
    segments: [
      {
        name: 'Focus Time',
        pct: focusPct,
        time: fmtTime(focusSec),
        color: '#22c55e',
      },
      {
        name: 'Distraction Time',
        pct: 100 - focusPct,
        time: fmtTime(otherSec),
        color: '#E5E7EB',
      },
    ],
  };
}

/**
 * Resolves the effective focus app list (via useEffectiveProductivityApps),
 * normalises raw OS paths via getAppNameByAppPath, and returns the widget data.
 *
 * @param {Record<string,number>|null|undefined} appUsage  raw path → seconds map
 * @param {number|null|undefined}                totalSec  total tracked seconds
 * @param {number|null|undefined}                activeSec active (productive) seconds — focus time is capped to this
 * @param {{ companyId?: string, teamId?: string }} ids
 * @returns {{ data: object|null, isLoading: boolean }}
 */
export function useFocusVsDistraction(
  appUsage,
  totalSec,
  activeSec,
  { companyId, teamId } = {}
) {
  const { data: appNameMapping = {} } = useQuery({
    queryKey: queryKeys.applicationInfo.mapping,
    queryFn: fetchApplicationInfoMapping,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const { focusApps, isLoading } = useEffectiveProductivityApps({
    companyId,
    teamId,
  });

  const data = useMemo(
    () => calculate(appUsage, totalSec, activeSec, focusApps, appNameMapping),
    [appUsage, totalSec, activeSec, focusApps, appNameMapping]
  );

  return { data, isLoading };
}
